import { ClientQuery } from '@core/client/client.query';
import { Client } from '@core/models/client.model';

export interface ProcessQueueOrgNameLoggedInUser {
  isClient?: boolean;
  isSuperUser?: boolean;
  partnerAdmin?: { uid?: string } | null;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  companyName?: string;
  company_name?: string;
  ddPartner?: { uid?: string; fullCompanyName?: string } | null;
  partnerAdminPartners?: Array<{ uid?: string; fullCompanyName?: string }> | null;
  client?: {
    type?: string;
    uid?: string;
    companyName?: string;
    company_name?: string;
    firstname?: string;
    surname?: string;
    first_name?: string;
    last_name?: string;
  } | null;
}

export interface ProcessQueueOrgNamePartnerRef {
  uid?: string;
  fullCompanyName?: string;
}

export interface ProcessQueueOrgNameContext {
  emittentCompanyName: unknown;
  issuerOrganizationName?: unknown;
  currentUserType?: unknown;
  activeClientId?: string | null;
  storedClientId?: string | null;
  currentRoute?: string | null;
  historyReturnUrl?: string | null;
  storedReturnUrl?: string | null;
  activeClient?: Client | null;
  clientQuery?: ClientQuery | null;
  loggedInUser?: ProcessQueueOrgNameLoggedInUser | null;
  partnerUid?: string | null;
  activeThemePartner?: ProcessQueueOrgNamePartnerRef | null;
  organizationPartners?: ProcessQueueOrgNamePartnerRef[] | null;
}

export function readActiveThemePartnerFromStorage(): ProcessQueueOrgNamePartnerRef | null {
  try {
    const raw = localStorage.getItem('activeTheme');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as ProcessQueueOrgNamePartnerRef;
    return parsed?.uid ? parsed : null;
  } catch {
    return null;
  }
}

export function resolvePartnerNameForOrgName(context: {
  partnerUid?: string | null;
  activeThemePartner?: ProcessQueueOrgNamePartnerRef | null;
  loggedInUser?: ProcessQueueOrgNameLoggedInUser | null;
  organizationPartners?: ProcessQueueOrgNamePartnerRef[] | null;
}): string | null {
  const partnerUid = String(context.partnerUid ?? '').trim();
  const activeThemePartner = context.activeThemePartner;

  if (activeThemePartner?.uid) {
    if (!partnerUid || activeThemePartner.uid === partnerUid) {
      const activeThemeName = String(activeThemePartner.fullCompanyName ?? '').trim();
      if (activeThemeName) {
        return activeThemeName;
      }
    }
  }

  const user = context.loggedInUser;
  if (partnerUid && user?.ddPartner?.uid === partnerUid) {
    const ddPartnerName = String(user.ddPartner.fullCompanyName ?? '').trim();
    if (ddPartnerName) {
      return ddPartnerName;
    }
  }

  const partnerAdminMatch = user?.partnerAdminPartners?.find((partner) => partner?.uid === partnerUid);
  if (partnerAdminMatch) {
    const partnerAdminName = String(partnerAdminMatch.fullCompanyName ?? '').trim();
    if (partnerAdminName) {
      return partnerAdminName;
    }
  }

  if (partnerUid && context.organizationPartners?.length) {
    const organizationPartner = context.organizationPartners.find((partner) => partner?.uid === partnerUid);
    const organizationPartnerName = String(organizationPartner?.fullCompanyName ?? '').trim();
    if (organizationPartnerName) {
      return organizationPartnerName;
    }
  }

  const fallbackActiveThemeName = String(activeThemePartner?.fullCompanyName ?? '').trim();
  return fallbackActiveThemeName || null;
}

export function resolveProcessQueueOrgName(context: ProcessQueueOrgNameContext): string {
  const fallback = String(context.emittentCompanyName ?? '').trim();

  const loggedInClientOrgName = resolveLoggedInClientOrgName(context);
  if (loggedInClientOrgName) {
    return loggedInClientOrgName;
  }

  const normalizedUserType = normalizeProcessQueueUserType(context);

  if (normalizedUserType === 'superuser' || normalizedUserType === 'partner_admin') {
    const partnerName = resolvePartnerNameForOrgName({
      partnerUid: context.partnerUid,
      activeThemePartner: context.activeThemePartner ?? readActiveThemePartnerFromStorage(),
      loggedInUser: context.loggedInUser,
      organizationPartners: context.organizationPartners,
    });
    return partnerName || fallback;
  }

  if (normalizedUserType === 'emittent_admin') {
    const issuerName = String(context.issuerOrganizationName ?? context.emittentCompanyName ?? '').trim();
    return issuerName || fallback;
  }

  return fallback;
}

function normalizeProcessQueueUserType(context: ProcessQueueOrgNameContext): string {
  if (isLoggedInClientUser(context)) {
    return 'client';
  }

  const explicitUserType = String(context.currentUserType ?? '').trim().toLowerCase();
  if (explicitUserType) {
    return explicitUserType;
  }

  const user = context.loggedInUser;
  if (user?.isSuperUser) {
    return 'superuser';
  }
  if (user?.partnerAdmin?.uid) {
    return 'partner_admin';
  }
  if (user?.isClient) {
    return 'client';
  }

  return '';
}

function isLoggedInClientUser(context: ProcessQueueOrgNameContext): boolean {
  const user = context.loggedInUser;
  return user?.isClient === true || String(context.currentUserType ?? '').trim().toLowerCase() === 'client';
}

function resolveLoggedInClientOrgName(context: ProcessQueueOrgNameContext): string | null {
  if (!isLoggedInClientUser(context)) {
    return null;
  }

  const user = context.loggedInUser;
  if (!user) {
    return null;
  }

  const clientEntity = resolveLoggedInClientEntity(context, user);
  const clientType = resolveLoggedInClientType(user, clientEntity);

  if (clientType === 'company') {
    return resolveCompanyClientOrgName(user, clientEntity);
  }

  if (clientType === 'person') {
    return resolvePersonClientOrgName(user, clientEntity);
  }

  return resolveCompanyClientOrgName(user, clientEntity) ?? resolvePersonClientOrgName(user, clientEntity);
}

function resolveLoggedInClientType(
  user: ProcessQueueOrgNameLoggedInUser,
  clientEntity: Client | null,
): 'company' | 'person' | '' {
  const explicitType = String(user.client?.type ?? clientEntity?.type ?? '')
    .trim()
    .toLowerCase();

  if (explicitType === 'company' || explicitType === 'person') {
    return explicitType;
  }

  if (resolveCompanyClientOrgName(user, clientEntity)) {
    return 'company';
  }

  if (resolvePersonClientOrgName(user, clientEntity)) {
    return 'person';
  }

  return '';
}

function resolveLoggedInClientEntity(
  context: ProcessQueueOrgNameContext,
  user: ProcessQueueOrgNameLoggedInUser,
): Client | null {
  const clientUid = String(user.client?.uid ?? '').trim();
  if (!clientUid) {
    return null;
  }

  if (context.activeClient?.id === clientUid) {
    return context.activeClient;
  }

  const activeFromQuery = context.clientQuery?.getActive?.() as Client | undefined;
  if (activeFromQuery?.id === clientUid) {
    return activeFromQuery;
  }

  if (context.clientQuery?.getEntity) {
    const fromStore = context.clientQuery.getEntity(clientUid) as Client | undefined;
    if (fromStore?.id) {
      return fromStore;
    }
  }

  return null;
}

function resolveCompanyClientOrgName(
  user: ProcessQueueOrgNameLoggedInUser,
  clientEntity: Client | null,
): string | null {
  const companyName = [
    clientEntity?.type === 'company' ? clientEntity.companyName : null,
    user.client?.companyName,
    user.client?.company_name,
    user.companyName,
    user.company_name,
  ]
    .map((value) => String(value ?? '').trim())
    .find(Boolean);

  return companyName ?? null;
}

function resolvePersonClientOrgName(
  user: ProcessQueueOrgNameLoggedInUser,
  clientEntity: Client | null,
): string | null {
  const personName = [
    clientEntity?.type === 'person' ? resolveClientEntityOrgName(clientEntity) : null,
    buildPersonName(user.client?.firstname, user.client?.surname),
    buildPersonName(user.client?.first_name, user.client?.last_name),
    buildPersonName(user.firstName, user.lastName),
    buildPersonName(user.first_name, user.last_name),
  ]
    .map((value) => String(value ?? '').trim())
    .find(Boolean);

  return personName ?? null;
}

function buildPersonName(firstName: unknown, lastName: unknown): string | null {
  const personName = `${String(firstName ?? '').trim()} ${String(lastName ?? '').trim()}`.trim();
  return personName || null;
}

function resolveClientEntityOrgName(client: Client): string | null {
  if (client.type === 'company') {
    const companyName = String(client.companyName ?? '').trim();
    return companyName || null;
  }

  if (client.type === 'person') {
    const personName = `${String(client.firstname ?? '').trim()} ${String(client.surname ?? '').trim()}`.trim();
    return personName || null;
  }

  return null;
}
